import { useEffect, useRef, useState } from 'preact/hooks';
import { media } from '../api';
import { startCheckout } from '../billing';
import { Button, Card, Countdown, Empty, Field, Modal, Pill, Tabs, toast } from '../ui';
import { MapView } from './MapView';
import type { GameSocket, Passage } from './socket';

const STATS = ['might', 'agility', 'wits', 'heart'] as const;

export function Feed(p: { passages: Passage[]; empty?: string }) {
	const end = useRef<HTMLDivElement>(null);
	useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [p.passages.length]);
	if (!p.passages.length) return <Empty>{p.empty ?? 'The story begins when the GM starts the game.'}</Empty>;
	let lastBeat: number | undefined;
	return (
		<div class="feed" aria-live="polite">
			{p.passages.map((x, i) => {
				const sep = x.beat !== undefined && x.beat !== lastBeat && i > 0;
				lastBeat = x.beat;
				return (
					<div key={`${x.moment}-${i}`}>
						{sep ? <div class="beat-sep">beat {x.beat}</div> : null}
						<p class={`passage passage-${x.role} kind-${x.kind} ${x.outcome ? `outcome-${x.outcome}` : ''}`}>
							{x.observed ? <span class="observed-tag">{x.observed}: </span> : null}
							{x.text}
						</p>
					</div>
				);
			})}
			<div ref={end} />
		</div>
	);
}

export function Play(p: { sock: GameSocket }) {
	const s = p.sock;
	const view = s.view.use();
	const you = s.you.use();
	const game = s.game.use();
	const feed = s.feed.use();
	const observed = s.observed.use();
	const proposals = s.proposals.use();
	const screen = s.screen.use();
	const ending = s.ending.use();
	const suggestion = s.suggestion.use();
	const [panel, setPanel] = useState<'sheet' | 'bag' | 'deck' | 'lore' | 'table' | 'map'>('sheet');
	if (!you) return null;
	if (!you.characterId || !view) return <ChooseCharacter sock={s} game={game} />;
	const area = view.area;
	return (
		<div class="play">
			<section class="scene" style={area.asset ? { '--scene-bg': `url(${media(area.asset, 1600)})` } : undefined}>
				<div class="scene-head">
					<div>
						<h2>{area.name}</h2>
						<p class="muted small">
							{area.dark ? 'Dark. ' : ''}
							{view.present.length ? `Here: ${view.present.map((c: any) => c.name).join(', ')}` : 'You are alone.'}
						</p>
					</div>
					{view.observedBy?.length ? <Pill tone="accent">👁 observed by {view.observedBy.join(', ')}</Pill> : null}
				</div>
				{you.status === 'observing' ? (
					<div class="observing">
						<p>
							<strong>Observing</strong> — out of character; nothing here becomes your character's knowledge.{' '}
							<Button small variant="ghost" onClick={() => s.send({ t: 'stop_observing' })}>
								Back to my character
							</Button>
						</p>
						<Feed passages={observed} empty="Waiting for something to happen…" />
					</div>
				) : (
					<Feed passages={feed} />
				)}
				{game?.status === 'lobby' ? <p class="callout">Waiting for the GM to start the game. Look over your character in the meantime.</p> : null}
				{game?.status === 'active' && you.status !== 'observing' ? <Composer sock={s} view={view} handMode={game.handMode} /> : null}
			</section>
			<aside class="side">
				<Tabs
					value={panel}
					onChange={setPanel}
					class="tabs-small"
					tabs={[
						{ id: 'sheet', label: 'Sheet' },
						{ id: 'bag', label: 'Bag', badge: view.me.inventory.length },
						{ id: 'deck', label: 'Deck' },
						{ id: 'lore', label: 'Lore' },
						{ id: 'table', label: 'Table' },
						{ id: 'map', label: 'Map' },
					]}
				/>
				{panel === 'sheet' ? <Sheet sock={s} view={view} /> : null}
				{panel === 'bag' ? <Bag sock={s} view={view} /> : null}
				{panel === 'deck' ? <Deck view={view} /> : null}
				{panel === 'lore' ? <Lore view={view} /> : null}
				{panel === 'table' ? <TablePanel sock={s} view={view} /> : null}
				{panel === 'map' ? <MiniMap view={view} /> : null}
			</aside>
			{proposals.length ? <ProposalPrompt sock={s} proposal={proposals[0]} /> : null}
			{view.me.pendingLevelUps > 0 ? <LevelUp sock={s} /> : null}
			{screen ? (
				<Modal title={screen.title} onClose={() => s.screen.set(null)} wide>
					{screen.asset ? <img class="screen-art" src={media(screen.asset, 1600)} alt="" /> : null}
					<p class="screen-text">{screen.text}</p>
				</Modal>
			) : null}
			{ending ? (
				<Modal title={ending.title} onClose={() => s.ending.set(null)} wide>
					{ending.asset ? <img class="screen-art" src={media(ending.asset, 1600)} alt="" /> : null}
					<p class={`result result-${ending.result}`}>{ending.result === 'victory' ? 'Victory' : ending.result === 'defeat' ? 'Defeat' : 'The End'}</p>
					<p>{ending.brief}</p>
					{ending.epilogue ? <p class="screen-text">{ending.epilogue}</p> : null}
				</Modal>
			) : null}
			{suggestion ? <Suggestion sock={s} s={suggestion} onClose={() => s.suggestion.set(null)} /> : null}
		</div>
	);
}

function ChooseCharacter(p: { sock: GameSocket; game: any }) {
	const [desc, setDesc] = useState('');
	if (!p.game) return null;
	return (
		<div class="page narrow">
			<Card title="Choose your character">
				{p.game.claimable.length ? (
					<div class="char-grid">
						{p.game.claimable.map((c: any) => (
							<div key={c.id} class="char-card">
								{c.asset ? <img src={media(c.asset, 320)} alt="" /> : null}
								<h3>{c.name}</h3>
								<p class="muted small">{c.role}{c.faction ? ` · ${c.faction}` : ''}</p>
								<p class="small">{c.description}</p>
								<Button small onClick={() => p.sock.send({ t: 'claim', character: c.id })}>
									Play {c.name}
								</Button>
							</div>
						))}
					</div>
				) : (
					<Empty>Every playable character is taken.</Empty>
				)}
				{p.game.allowOriginalCharacters ? (
					<>
						<h3>Or bring your own</h3>
						<Field label="Describe your character" hint="A sentence or two. The GM may need to approve it.">
							<textarea rows={3} value={desc} onInput={(e) => setDesc(e.currentTarget.value)} maxLength={1500} />
						</Field>
						<Button variant="ghost" disabled={desc.trim().length < 10} onClick={() => p.sock.send({ t: 'create_character', description: desc })}>
							Create character
						</Button>
					</>
				) : null}
			</Card>
		</div>
	);
}

function Composer(p: { sock: GameSocket; view: any; handMode: boolean }) {
	const s = p.sock;
	const timerTotal = s.game.use()?.timerSeconds ?? 60;
	const skew = s.skew.use();
	const [text, setText] = useState('');
	const [card, setCard] = useState<string | null>(null);
	const lastSent = useRef({ at: 0, text: '' });
	const v = p.view;
	const acted = v.acted;
	const waiting = v.scene?.state && v.scene.state !== 'collecting';
	// Typing heartbeat: ~every 2 seconds while the draft changes (resets the server-side timer).
	useEffect(() => {
		const t = setInterval(() => {
			if (text !== lastSent.current.text && Date.now() - lastSent.current.at > 1900) {
				lastSent.current = { at: Date.now(), text };
				s.send({ t: 'typing', draft: text });
			}
		}, 500);
		return () => clearInterval(t);
	}, [text]);
	const submit = (payload: Record<string, unknown>) => {
		s.send({ t: 'submit', ...payload, ...(card ? { card } : {}) });
		setText('');
		setCard(null);
		lastSent.current = { at: Date.now(), text: '' };
	};
	const act = (intent: Record<string, unknown>) => submit({ intents: [intent] });
	const hand = v.me.deck?.hand ?? [];
	if (v.cannotAct) return <p class="callout">You can't act right now ({v.cannotAct}).</p>;
	return (
		<div class="composer">
			<div class="composer-status">
				{acted ? <span class="muted">You've acted. Waiting for the others…</span> : waiting ? <span class="muted">Resolving…</span> : v.timer ? <Countdown deadline={v.timer.deadline} skew={-skew} total={timerTotal} /> : null}
			</div>
			{p.handMode && hand.length ? (
				<div class="hand" role="radiogroup" aria-label="Your hand">
					{hand.map((c: any) => (
						<button key={c.id} class={`card-chip ${card === c.id ? 'card-on' : ''} suit-${c.suit ?? 'none'}`} onClick={() => setCard(card === c.id ? null : c.id)}>
							{c.kind === 'normal' || c.kind === 'lent' ? `${c.value >= 0 ? '+' : ''}${c.value}` : c.label ?? c.kind}
							<small>{c.suit ?? ''}</small>
						</button>
					))}
				</div>
			) : null}
			<form
				class="composer-form"
				onSubmit={(e) => {
					e.preventDefault();
					if (text.trim()) submit({ text });
				}}
			>
				<textarea
					rows={2}
					value={text}
					disabled={acted}
					placeholder={acted ? 'Your action is in.' : 'What do you do? (e.g. "I hold the door", say "Run!")'}
					onInput={(e) => setText(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							if (text.trim()) submit({ text });
						}
					}}
					aria-label="Your action"
				/>
				<div class="composer-buttons">
					<Button type="submit" disabled={acted || !text.trim()}>
						Act
					</Button>
					<Button type="button" variant="quiet" disabled={acted} onClick={() => act({ verb: 'wait' })}>
						Wait
					</Button>
				</div>
			</form>
			{!acted ? (
				<div class="quick">
					{v.exits.map((x: any) => (
						<button key={x.id} class="chip" disabled={x.state !== 'open'} onClick={() => act({ verb: 'move', target: x.id })} title={x.state !== 'open' ? x.state : `Go to ${x.toName}`}>
							→ {x.name}
							{x.state === 'locked' ? ' 🔒' : ''}
						</button>
					))}
					{v.items.map((i: any) => (
						<button key={i.id} class="chip" onClick={() => act({ verb: 'take', target: i.id })}>
							Take {i.name}
						</button>
					))}
					{v.area.features.map((f: any) => (
						<button key={f.id} class="chip" onClick={() => act({ verb: 'examine', target: f.id })}>
							Examine {f.name}
						</button>
					))}
					<button class="chip" onClick={() => act({ verb: 'examine' })}>
						Look around
					</button>
					{v.me.relations.length ? (
						<button class="chip" onClick={() => act({ verb: 'release' })}>
							Let go / climb down
						</button>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function Sheet(p: { sock: GameSocket; view: any }) {
	const me = p.view.me;
	return (
		<div class="panel">
			<div class="sheet-head">
				{me.asset ? <img class="portrait" src={media(me.asset, 320)} alt="" /> : null}
				<div>
					<h3>{me.name}</h3>
					<p class="muted small">
						{me.role}
						{me.faction ? ` · ${me.faction}` : ''} · level {me.level} · {me.xp} xp
					</p>
				</div>
			</div>
			<div class="stats">
				{STATS.map((k) => (
					<div key={k} class="stat">
						<span>{k}</span>
						<strong>{me.stats[k]}</strong>
					</div>
				))}
			</div>
			<p class="small">
				Wounds {me.wounds} · Stamina {me.stamina}/{me.maxStamina}
			</p>
			{me.conditions.length ? (
				<p>
					{me.conditions.map((c: any) => (
						<Pill key={c.name} tone="warn">
							{c.name}
						</Pill>
					))}
				</p>
			) : null}
			{me.relations.length ? <p class="small">{me.relations.map((r: any) => `${r.type.replace('_', ' ')} ${r.a === me.id ? r.otherName : `(by ${r.otherName})`}`).join(' · ')}</p> : null}
			<h4>Traits</h4>
			{me.traits.length ? (
				<ul class="list">
					{me.traits.map((t: any) => (
						<li key={t.id} class="row-between">
							<span>
								{t.summary} {t.kind === 'temporary' ? <Pill>for now</Pill> : null} {t.cardId ? <Pill tone="accent">mechanical</Pill> : null}
							</span>
							{t.source === 'player' ? (
								<button class="linkish small" onClick={() => p.sock.send({ t: 'trait_remove', trait: t.id })}>
									remove
								</button>
							) : null}
						</li>
					))}
				</ul>
			) : (
				<p class="muted small">Say things about your character ("I'm afraid of heights") and the world will remember.</p>
			)}
			<p class="small muted">{me.description}</p>
			{me.goals?.length ? <p class="small">Goals: {me.goals.join('; ')}</p> : null}
		</div>
	);
}

function Bag(p: { sock: GameSocket; view: any }) {
	const v = p.view;
	const others = v.present.filter((c: any) => c.alive);
	const act = (intent: any) => p.sock.send({ t: 'submit', intents: [intent] });
	return (
		<div class="panel">
			{v.me.inventory.length ? (
				<ul class="list">
					{v.me.inventory.map((i: any) => (
						<li key={i.id}>
							<strong>{i.name}</strong>
							<p class="small muted">{i.description}</p>
							{i.contents.length ? <p class="small">Inside: {i.contents.map((c: any) => c.name).join(', ')}</p> : null}
							<div class="row wrap">
								<button class="chip" disabled={v.acted} onClick={() => act({ verb: 'use', item: i.id })}>
									Use
								</button>
								{v.exits
									.filter((x: any) => x.state === 'locked')
									.map((x: any) => (
										<button key={x.id} class="chip" disabled={v.acted} onClick={() => act({ verb: 'use', item: i.id, target: x.id })}>
											Use on {x.name}
										</button>
									))}
								<button class="chip" disabled={v.acted} onClick={() => act({ verb: 'drop', target: i.id })}>
									Drop
								</button>
								{others.map((c: any) => (
									<button key={c.id} class="chip" disabled={v.acted} onClick={() => act({ verb: 'give', item: i.id, target: c.id })}>
										Give to {c.name}
									</button>
								))}
							</div>
						</li>
					))}
				</ul>
			) : (
				<Empty>You carry nothing.</Empty>
			)}
		</div>
	);
}

function Deck(p: { view: any }) {
	const d = p.view.me.deck;
	if (!d) return <Empty>No personal deck.</Empty>;
	return (
		<div class="panel">
			<p class="small">
				{d.remaining} of {d.size} cards left before the reshuffle · {d.discard} in the discard. Luck evens out: the good cards are still coming.
			</p>
			{[...STATS, 'none'].map((suit) => (
				<div key={suit} class="deck-row">
					<span class="deck-suit">{suit === 'none' ? 'special' : suit}</span>
					<span class="deck-cards">
						{(d.remainingBySuit[suit] ?? []).map((v: any, i: number) => (
							<span key={i} class={`card-mini ${typeof v === 'number' ? (v > 0 ? 'pos' : v < 0 ? 'neg' : '') : 'special'}`}>
								{typeof v === 'number' ? (v > 0 ? `+${v}` : v) : v}
							</span>
						))}
					</span>
				</div>
			))}
		</div>
	);
}

function Lore(p: { view: any }) {
	return (
		<div class="panel">
			<h4>What you know</h4>
			<ul class="list">
				{p.view.me.knowledge.map((k: any) => (
					<li key={k.id} class="small">
						{k.text}
					</li>
				))}
			</ul>
			<h4>Here</h4>
			<p class="small">{p.view.area.description}</p>
		</div>
	);
}

function TablePanel(p: { sock: GameSocket; view: any }) {
	const players = p.sock.players.use();
	const you = p.sock.you.use();
	const game = p.sock.game.use();
	const [switching, setSwitching] = useState(false);
	const act = (intent: any) => p.sock.send({ t: 'submit', intents: [intent] });
	return (
		<div class="panel">
			<h4>Players</h4>
			<ul class="list">
				{players.map((pl: any) => (
					<li key={pl.userId} class="row-between">
						<span>
							<span class={`dot ${pl.online ? 'dot-on' : ''}`} /> {pl.name}
							{pl.characterName ? ` as ${pl.characterName}` : ''} {pl.role === 'gm' ? <Pill tone="accent">GM</Pill> : null} {pl.status === 'observing' ? <Pill>observing</Pill> : null}
						</span>
						{game?.observingAllowed && pl.userId !== you?.userId && pl.characterId && you?.status !== 'observing' ? (
							<button class="linkish small" onClick={() => p.sock.send({ t: 'observe', follow: pl.userId })}>
								observe
							</button>
						) : null}
					</li>
				))}
			</ul>
			<h4>Here with you</h4>
			<ul class="list">
				{p.view.present.map((c: any) => (
					<li key={c.id}>
						<strong>{c.name}</strong> <span class="muted small">{c.role}</span> {c.isPlayer ? <Pill tone="accent">player</Pill> : null} {c.conditions.map((x: string) => <Pill key={x}>{x}</Pill>)}
						<p class="small muted">{c.description}</p>
						<div class="row wrap">
							<button class="chip" disabled={p.view.acted} onClick={() => act({ verb: 'propose_joint', target: c.id, relation: 'holding' })}>
								Take hand
							</button>
							<button class="chip" disabled={p.view.acted} onClick={() => act({ verb: 'propose_joint', target: c.id, relation: 'carrying' })}>
								Offer to carry
							</button>
							<button class="chip" disabled={p.view.acted} onClick={() => act({ verb: 'propose_joint', target: c.id, relation: 'following' })}>
								Follow
							</button>
							<button class="chip" disabled={p.view.acted} onClick={() => act({ verb: 'examine', target: c.id })}>
								Look at
							</button>
							<button class="chip danger" disabled={p.view.acted} onClick={() => confirm(`Attack ${c.name}?`) && act({ verb: 'attack', target: c.id })}>
								Attack
							</button>
						</div>
					</li>
				))}
			</ul>
			{game?.switching !== 'off' ? (
				<>
					<Button small variant="ghost" onClick={() => setSwitching(true)}>
						Switch character…
					</Button>
					{switching ? <SwitchPicker sock={p.sock} onClose={() => setSwitching(false)} /> : null}
				</>
			) : null}
			{you?.originalCharacter ? (
				<Button small variant="ghost" onClick={() => p.sock.send({ t: 'switch_back' })}>
					Switch back to my character
				</Button>
			) : null}
			<Button
				small
				variant="ghost"
				onClick={async () => {
					try {
						if (await startCheckout({ kind: 'gift', code: game.code })) toast('Thanks for chipping in!', 'ok');
					} catch (e) {
						toast((e as Error).message, 'warn');
					}
				}}
			>
				Chip in for the GM's beats
			</Button>
			<Button
				small
				variant="quiet"
				onClick={() => {
					if (confirm('Leave this game? Your character steps back to the GM.')) fetch(`/api/games/${game.code}/leave`, { method: 'POST' }).then(() => (location.href = '/home'));
				}}
			>
				Leave game
			</Button>
		</div>
	);
}

function SwitchPicker(p: { sock: GameSocket; onClose: () => void }) {
	const game = p.sock.game.use();
	return (
		<Modal title="Switch character" onClose={p.onClose}>
			<p class="small">Knowledge stays with characters: your new character only knows what they know. Your current character stays behind as an NPC.</p>
			{game.switchable.length ? (
				<ul class="list">
					{game.switchable.map((c: any) => (
						<li key={c.id} class="row-between">
							<span>
								{c.name} <span class="muted small">{c.role}</span>
							</span>
							<Button
								small
								onClick={() => {
									p.sock.send({ t: 'switch_request', character: c.id });
									p.onClose();
								}}
							>
								Switch
							</Button>
						</li>
					))}
				</ul>
			) : (
				<Empty>No characters are free to switch to.</Empty>
			)}
		</Modal>
	);
}

function MiniMap(p: { view: any }) {
	const v = p.view;
	const areas = [{ id: v.area.id, name: v.area.name, joinPoint: true }, ...v.exits.map((x: any) => ({ id: x.to, name: x.toName }))];
	const uniq = areas.filter((a, i) => areas.findIndex((b) => b.id === a.id) === i);
	const exits = v.exits.map((x: any) => ({ id: x.id, from: v.area.id, to: x.to, name: x.name, state: x.state }));
	return (
		<div class="panel">
			<MapView areas={uniq} exits={exits} here={v.area.id} compact />
			<p class="small muted">You only know the ways out of where you stand.</p>
		</div>
	);
}

function ProposalPrompt(p: { sock: GameSocket; proposal: any }) {
	const [words, setWords] = useState('');
	const pr = p.proposal;
	const answer = (a: string, text?: string) => {
		p.sock.send({ t: 'answer', proposal: pr.id, answer: a, text });
		p.sock.proposals.update((ps) => ps.filter((x) => x.id !== pr.id));
	};
	const attempt = pr.kind === 'attempt';
	return (
		<Modal title={attempt ? 'React!' : `${pr.fromName ?? 'Someone'} asks`}>
			<p class="proposal-q">{pr.question}</p>
			<div class="row wrap">
				{attempt ? (
					<>
						<Button onClick={() => answer('go_with')}>Go with it</Button>
						<Button variant="ghost" onClick={() => answer('brace')}>
							Brace
						</Button>
						<Button variant="ghost" onClick={() => answer('dodge')}>
							Dodge
						</Button>
					</>
				) : (
					<>
						<Button onClick={() => answer('accept')}>Accept</Button>
						<Button variant="ghost" onClick={() => answer('decline')}>
							Decline
						</Button>
					</>
				)}
			</div>
			{!attempt ? (
				<form
					class="row"
					onSubmit={(e) => {
						e.preventDefault();
						if (words.trim()) answer('counter', words);
					}}
				>
					<input value={words} onInput={(e) => setWords(e.currentTarget.value)} placeholder="…or answer in your own words" aria-label="Answer in your own words" />
					<Button type="submit" variant="ghost" disabled={!words.trim()}>
						Say it
					</Button>
				</form>
			) : null}
		</Modal>
	);
}

function LevelUp(p: { sock: GameSocket }) {
	const [suit, setSuit] = useState('might');
	return (
		<Modal title="Level up!">
			<p>Your deck grows with you. Choose one:</p>
			<div class="row">
				<select value={suit} onChange={(e) => setSuit(e.currentTarget.value)} aria-label="Suit">
					{STATS.map((s) => (
						<option key={s}>{s}</option>
					))}
				</select>
				<Button onClick={() => p.sock.send({ t: 'level_choice', choice: { kind: 'add', suit } })}>Add a +3 {suit} card</Button>
			</div>
			<Button variant="ghost" onClick={() => p.sock.send({ t: 'level_choice', choice: { kind: 'remove' } })}>
				Remove a −2 card instead
			</Button>
		</Modal>
	);
}

function Suggestion(p: { sock: GameSocket; s: any; onClose: () => void }) {
	const players = p.sock.players.use();
	const you = p.sock.you.use();
	if (p.s.kind === 'switch_back')
		return (
			<Modal title="Your character can act again" onClose={p.onClose}>
				<p>{p.s.name} is free. Switch back?</p>
				<Button
					onClick={() => {
						p.sock.send({ t: 'switch_back' });
						p.onClose();
					}}
				>
					Switch back
				</Button>
			</Modal>
		);
	return (
		<Modal title="You can't act right now" onClose={p.onClose}>
			<p>Your character is {p.s.reason}. While you wait you can observe another player, out of character.</p>
			{p.s.observing ? (
				<div class="row wrap">
					{players
						.filter((pl: any) => pl.userId !== you?.userId && pl.characterId)
						.map((pl: any) => (
							<Button
								key={pl.userId}
								small
								variant="ghost"
								onClick={() => {
									p.sock.send({ t: 'observe', follow: pl.userId });
									p.onClose();
								}}
							>
								Observe {pl.name}
							</Button>
						))}
				</div>
			) : null}
			{p.s.switching ? <p class="small">Or switch to another character from the Table panel.</p> : null}
			<p class="small muted">Anything that happens to your character pulls you straight back.</p>
		</Modal>
	);
}
